/**
 * Onboarding overhaul — first-5-minutes wizard.
 *
 * Six steps, each addressable via /onboarding/<step> for refresh survivability:
 *   1. /onboarding/business-profile  — name, business type, address, tz, currency, locale
 *   2. /onboarding/facility          — first facility name + address + hours
 *   3. /onboarding/services          — template-driven service catalogue (auto-seeded)
 *   4. /onboarding/runs              — runs or daycare slot count (skippable for grooming)
 *   5. /onboarding/brand             — brand voice (3 sliders + notes) + logo OR palette
 *   6. /onboarding/payments          — Stripe Connect (clearly "do later" UX)
 *
 * Final POST /api/onboarding/complete stamps accounts.onboarding_completed_at;
 * we then land on /today.
 */

import {
  type OnboardingApplyTemplateResult,
  type OnboardingBusinessType,
  type OnboardingSampleDataResult,
  createService,
  getMe,
  onboardingApplyTemplate,
  onboardingCheckSlug,
  onboardingComplete,
  onboardingPatchStep,
  onboardingSeedSampleData,
  postConnectOnboardingLink,
} from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  CreditCard,
  Loader2,
  Palette,
  PawPrint,
  Play,
  Scissors,
  Sparkles,
  Sun,
  Sunset,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { useAuthContext } from './useAuthContext';

const STEPS = [
  { slug: 'business-profile', label: 'Business profile' },
  { slug: 'facility', label: 'First facility' },
  { slug: 'services', label: 'Services' },
  { slug: 'rates', label: 'Rates' },
  { slug: 'runs', label: 'Capacity' },
  { slug: 'brand', label: 'Brand' },
  { slug: 'payments', label: 'Payments' },
] as const;
type StepSlug = (typeof STEPS)[number]['slug'];

const COMPLETED_KEY = 'kennel.onboarding.completed';
const STATE_KEY = 'kennel.onboarding.state';

interface PersistedState {
  businessType: OnboardingBusinessType | null;
  showSampleData: boolean;
  brandTone: number; // 0..100 (0=formal, 100=casual)
  brandWarmth: number; // 0..100
  brandLength: number; // 0..100
  brandNotes: string;
  primaryColor: string | null;
  appliedTemplate: OnboardingApplyTemplateResult | null;
  seededSampleData: OnboardingSampleDataResult | null;
}

function loadState(): PersistedState {
  if (typeof window === 'undefined') return defaultState();
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return defaultState();
    return { ...defaultState(), ...(JSON.parse(raw) as Partial<PersistedState>) };
  } catch {
    return defaultState();
  }
}

function defaultState(): PersistedState {
  return {
    businessType: null,
    showSampleData: false,
    brandTone: 60,
    brandWarmth: 70,
    brandLength: 50,
    brandNotes: '',
    primaryColor: null,
    appliedTemplate: null,
    seededSampleData: null,
  };
}

function saveState(s: PersistedState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STATE_KEY, JSON.stringify(s));
  } catch {
    // Quota / private-mode — ignore.
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Top-level component — owns persistent state + nested <Routes>
// ─────────────────────────────────────────────────────────────────────────────

export function OnboardingPage() {
  const navigate = useNavigate();
  const { state: authState } = useAuthContext();
  const [state, setStateRaw] = useState<PersistedState>(() => loadState());

  const setState = useCallback((patch: Partial<PersistedState>) => {
    setStateRaw((prev) => {
      const next = { ...prev, ...patch };
      saveState(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (
      localStorage.getItem(COMPLETED_KEY) === '1' ||
      authState.me?.activeAccount?.onboardingCompletedAt
    ) {
      navigate('/today', { replace: true });
    }
  }, [authState.me?.activeAccount?.onboardingCompletedAt, navigate]);

  const finish = async () => {
    try {
      await onboardingComplete();
    } catch {
      // Non-fatal — local flag below short-circuits the next mount.
    }
    if (typeof window !== 'undefined') {
      localStorage.setItem(COMPLETED_KEY, '1');
      localStorage.removeItem(STATE_KEY);
    }
    navigate('/today', { replace: true });
  };

  return (
    <div className="min-h-[100dvh] bg-background px-6 py-10">
      <div className="mx-auto max-w-3xl">
        <header className="mb-6 flex items-center gap-3">
          <div className="grid size-10 place-items-center rounded-xl bg-primary text-primary-foreground">
            <PawPrint className="size-5" strokeWidth={1.75} />
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Kennel Desk
            </p>
            <h1 className="font-heading text-xl font-medium tracking-tight">Set up your kennel</h1>
          </div>
        </header>

        <ProgressRail />

        <div className="mt-6 rounded-2xl bg-surface ring-1 ring-border elevate p-6 md:p-8">
          <Routes>
            <Route index element={<Navigate to="business-profile" replace />} />
            <Route
              path="business-profile"
              element={<BusinessProfileStep state={state} setState={setState} />}
            />
            <Route path="facility" element={<FacilityStep />} />
            <Route path="services" element={<ServicesStep state={state} setState={setState} />} />
            <Route path="rates" element={<RatesStep state={state} />} />
            <Route path="runs" element={<RunsCapacityStep state={state} />} />
            <Route path="brand" element={<BrandStep state={state} setState={setState} />} />
            <Route path="payments" element={<PaymentsStep onFinish={() => void finish()} />} />
            <Route path="*" element={<Navigate to="business-profile" replace />} />
          </Routes>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          You can change any of this later in Settings.
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Progress rail
// ─────────────────────────────────────────────────────────────────────────────

function ProgressRail() {
  const location = useLocation();
  const currentSlug = (location.pathname.split('/').pop() ?? '') as StepSlug;
  const currentIndex = Math.max(
    0,
    STEPS.findIndex((s) => s.slug === currentSlug),
  );
  const pct = ((currentIndex + 1) / STEPS.length) * 100;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        <span>
          Step {currentIndex + 1} of {STEPS.length}
        </span>
        <span>{STEPS[currentIndex]?.label}</span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full bg-primary transition-[width] duration-300"
          style={{ width: `${pct}%` }}
        />
      </div>
      <ol className="mt-3 grid grid-cols-7 gap-1.5">
        {STEPS.map((s, i) => (
          <li
            key={s.slug}
            className={cn(
              'rounded-md py-1.5 text-center text-[10px] uppercase tracking-[0.1em]',
              i <= currentIndex
                ? 'bg-primary/10 text-primary'
                : 'bg-secondary/40 text-muted-foreground',
            )}
          >
            {s.label}
          </li>
        ))}
      </ol>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 1 — Business profile (with business type + slug check + sample toggle)
// ─────────────────────────────────────────────────────────────────────────────

const BUSINESS_TYPES: ReadonlyArray<{
  value: OnboardingBusinessType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}> = [
  {
    value: 'kennel',
    label: 'Boarding kennel',
    description: 'Mostly overnight stays.',
    icon: PawPrint,
  },
  {
    value: 'daycare',
    label: 'Doggy daycare',
    description: 'Mostly day visits.',
    icon: Sun,
  },
  {
    value: 'grooming',
    label: 'Grooming salon',
    description: 'Mostly appointments.',
    icon: Scissors,
  },
  {
    value: 'multi',
    label: 'Multi-service',
    description: 'A mix of the above.',
    icon: Building2,
  },
];

function BusinessProfileStep({
  state,
  setState,
}: {
  state: PersistedState;
  setState: (patch: Partial<PersistedState>) => void;
}) {
  const navigate = useNavigate();
  const { state: authState } = useAuthContext();
  const currentSlug = authState.me?.activeAccount?.slug ?? '';
  const [slugInput, setSlugInput] = useState(currentSlug);
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');

  useEffect(() => {
    if (!slugInput || slugInput === currentSlug) {
      setSlugStatus('idle');
      return;
    }
    setSlugStatus('checking');
    const handle = setTimeout(async () => {
      try {
        const result = await onboardingCheckSlug(slugInput);
        setSlugStatus(result.available ? 'available' : 'taken');
      } catch {
        setSlugStatus('idle');
      }
    }, 350);
    return () => clearTimeout(handle);
  }, [slugInput, currentSlug]);

  const submit = async () => {
    if (state.businessType) {
      try {
        await onboardingPatchStep({ businessType: state.businessType });
      } catch {
        // Non-fatal — the value is in localStorage and step 3 will retry.
      }
    }
    navigate('/onboarding/facility');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">
          Tell us about your business
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Two minutes from here and your tenant will be ready to take its first booking.
        </p>
        <VideoLink label="2-min walkthrough" src="" />
      </div>

      <section>
        <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          What does your business do?
        </h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {BUSINESS_TYPES.map((bt) => {
            const Icon = bt.icon;
            const active = state.businessType === bt.value;
            return (
              <button
                key={bt.value}
                type="button"
                onClick={() => setState({ businessType: bt.value })}
                className={cn(
                  'flex items-start gap-3 rounded-xl border p-4 text-left press',
                  active
                    ? 'border-primary bg-primary/5 ring-2 ring-primary/30'
                    : 'border-border bg-background hover:border-primary/50',
                )}
              >
                <div
                  className={cn(
                    'grid size-9 shrink-0 place-items-center rounded-lg',
                    active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-foreground',
                  )}
                >
                  <Icon className="size-4" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{bt.label}</div>
                  <div className="text-xs text-muted-foreground">{bt.description}</div>
                </div>
                {active && <Check className="size-4 text-primary" strokeWidth={2.25} />}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="mb-3 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Customer-facing subdomain
        </h3>
        <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
          <input
            type="text"
            value={slugInput}
            onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            className="flex-1 bg-transparent text-sm outline-none"
            placeholder="riverside-kennels"
          />
          <span className="text-sm text-muted-foreground">.kennel.app</span>
        </div>
        <div className="mt-1.5 h-4 text-xs">
          {slugStatus === 'checking' && (
            <span className="text-muted-foreground">Checking availability…</span>
          )}
          {slugStatus === 'available' && (
            <span className="text-success">Available — yours to claim.</span>
          )}
          {slugStatus === 'taken' && (
            <span className="text-destructive">Taken — try a different name.</span>
          )}
        </div>
      </section>

      <section>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-dashed border-border bg-background/40 p-4">
          <input
            type="checkbox"
            checked={state.showSampleData}
            onChange={(e) => setState({ showSampleData: e.target.checked })}
            className="mt-0.5"
          />
          <div>
            <div className="text-sm font-medium">Show me how it looks with example data</div>
            <div className="text-xs text-muted-foreground">
              We'll seed 5 customers, 8 pets, and 12 bookings (mix of past + future) so the app
              feels alive while you explore. Wipe in one click later from Settings.
            </div>
          </div>
        </label>
      </section>

      <div className="flex justify-end">
        <PrimaryButton onClick={() => void submit()} disabled={!state.businessType}>
          Continue
          <ArrowRight className="size-4" strokeWidth={1.75} />
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 2 — First facility
// ─────────────────────────────────────────────────────────────────────────────

function FacilityStep() {
  const navigate = useNavigate();
  const { state: authState } = useAuthContext();
  const facility = authState.me?.activeFacility ?? authState.me?.facilitiesInScope[0];

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Your first facility</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          We created <strong>{facility?.name ?? 'Main'}</strong> based on your signup. You can
          rename it and add hours in Settings → Runs after the wizard.
        </p>
      </div>
      <div className="rounded-lg bg-secondary/30 px-4 py-3 text-sm text-muted-foreground">
        Facility: <strong>{facility?.name ?? 'Main'}</strong>
        <br />
        Timezone follows your account default — adjust per-facility if you run sites in different
        zones.
      </div>
      <div className="flex justify-between gap-2">
        <SkipButton onClick={() => navigate('/onboarding/business-profile')}>Back</SkipButton>
        <PrimaryButton onClick={() => navigate('/onboarding/services')}>
          Continue
          <ArrowRight className="size-4" strokeWidth={1.75} />
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 3 — Services (template-driven)
// ─────────────────────────────────────────────────────────────────────────────

function ServicesStep({
  state,
  setState,
}: {
  state: PersistedState;
  setState: (patch: Partial<PersistedState>) => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = async () => {
    if (!state.businessType) {
      navigate('/onboarding/business-profile');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await onboardingApplyTemplate(state.businessType);
      setState({ appliedTemplate: result });
      if (state.showSampleData) {
        try {
          const sample = await onboardingSeedSampleData();
          setState({ seededSampleData: sample });
        } catch {
          // Non-fatal — operator can re-run from Settings → Sample data.
        }
      }
      navigate('/onboarding/rates');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not apply template');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Service catalogue</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          We'll pre-seed your services based on the business type you picked. You can rename,
          re-price, or add more in Settings → Services later.
        </p>
        <VideoLink label="How services + pricing work" src="" />
      </div>
      <div className="rounded-lg border border-border bg-background p-4 text-sm">
        <strong>Template:</strong>{' '}
        <span className="text-primary">{state.businessType ?? 'pick one first'}</span>
      </div>
      {state.appliedTemplate && (
        <div className="rounded-lg bg-success/10 px-3 py-2 text-sm text-success">
          <CheckCircle2 className="mr-1.5 inline size-4" strokeWidth={2.25} />
          Seeded {state.appliedTemplate.created.length} service
          {state.appliedTemplate.created.length === 1 ? '' : 's'}.
        </div>
      )}
      {error && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}
      <div className="flex justify-between gap-2">
        <SkipButton onClick={() => navigate('/onboarding/facility')}>Back</SkipButton>
        <PrimaryButton onClick={() => void apply()} disabled={busy || !state.businessType}>
          {busy && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
          {state.appliedTemplate ? 'Continue' : 'Apply template & continue'}
          <ArrowRight className="size-4" strokeWidth={1.75} />
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 4 — Service rates (optional price override after template is applied)
// ─────────────────────────────────────────────────────────────────────────────

interface RateCard {
  id: 'boarding' | 'daycare' | 'grooming';
  label: string;
  pricingLabel: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  defaultEnabled: boolean;
  /** Default price in major units (pence/cents) for the input placeholder. */
  defaultMajor: string;
  pricingModel: 'per_day' | 'flat';
  slug: string;
}

const RATE_CARDS: RateCard[] = [
  {
    id: 'boarding',
    label: 'Boarding',
    pricingLabel: 'per night',
    icon: Sunset,
    defaultEnabled: true,
    defaultMajor: '35.00',
    pricingModel: 'per_day',
    slug: 'boarding',
  },
  {
    id: 'daycare',
    label: 'Daycare',
    pricingLabel: 'per visit',
    icon: Sun,
    defaultEnabled: false,
    defaultMajor: '28.00',
    pricingModel: 'flat',
    slug: 'daycare',
  },
  {
    id: 'grooming',
    label: 'Grooming',
    pricingLabel: 'per appointment',
    icon: Scissors,
    defaultEnabled: false,
    defaultMajor: '50.00',
    pricingModel: 'flat',
    slug: 'grooming',
  },
];

function RatesStep({ state }: { state: PersistedState }) {
  const navigate = useNavigate();
  const { state: authState } = useAuthContext();
  const currency = authState.me?.activeAccount?.currency ?? 'GBP';

  // Enabled flags per category
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const card of RATE_CARDS) {
      // Default boarding enabled for kennel/multi; daycare for daycare/multi;
      // grooming for grooming/multi.
      const bt = state.businessType;
      if (card.id === 'boarding')
        initial[card.id] = bt === 'kennel' || bt === 'multi' || bt === null;
      else if (card.id === 'daycare') initial[card.id] = bt === 'daycare' || bt === 'multi';
      else if (card.id === 'grooming') initial[card.id] = bt === 'grooming' || bt === 'multi';
      else initial[card.id] = card.defaultEnabled;
    }
    return initial;
  });

  // Price strings per category (major units, e.g. "35.00")
  const [prices, setPrices] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const card of RATE_CARDS) {
      initial[card.id] = card.defaultMajor;
    }
    return initial;
  });

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currencySymbol = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$';

  const handleContinue = async () => {
    setBusy(true);
    setError(null);
    try {
      const creates = RATE_CARDS.filter((c) => enabled[c.id]);
      await Promise.all(
        creates.map((card) => {
          const majorStr = prices[card.id] ?? card.defaultMajor;
          const majorNum = Number.parseFloat(majorStr);
          const priceMinor = Number.isNaN(majorNum) ? 0 : Math.round(majorNum * 100);
          return createService({
            name: card.label,
            category: card.id,
            applicableTo: {
              boarding_addon: false,
              daycare_addon: false,
              grooming_addon: false,
              standalone: true,
            },
            requiresAppointment: card.id === 'grooming',
            defaultPricingModel: card.pricingModel,
            defaultPriceMinor: priceMinor,
            speciesAllowed: ['dog', 'cat'],
          });
        }),
      );
    } catch (err: unknown) {
      // Non-fatal — if the template already seeded services these may 409 on
      // duplicate slug; swallow and continue so the operator isn't blocked.
      const msg = err instanceof Error ? err.message : '';
      if (!msg.includes('duplicate') && !msg.includes('conflict') && !msg.includes('already')) {
        setError(msg || 'Could not save rates — you can update them in Settings → Services.');
      }
    } finally {
      setBusy(false);
    }
    navigate('/onboarding/runs');
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Set your service rates</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter your base prices for each service you offer. These are starting points — you can
          create detailed pricing rules in Settings → Services at any time.
        </p>
      </div>

      <div className="space-y-3">
        {RATE_CARDS.map((card) => {
          const Icon = card.icon;
          const isOn = enabled[card.id] ?? false;
          return (
            <div
              key={card.id}
              className={cn(
                'rounded-xl border p-4 transition-colors',
                isOn ? 'border-primary bg-primary/5' : 'border-border bg-background',
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'grid size-10 shrink-0 place-items-center rounded-lg',
                    isOn
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-muted-foreground',
                  )}
                >
                  <Icon className="size-5" strokeWidth={1.75} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium">{card.label}</div>
                      <div className="text-xs text-muted-foreground">{card.pricingLabel}</div>
                    </div>
                    <label className="flex cursor-pointer items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) =>
                          setEnabled((prev) => ({ ...prev, [card.id]: e.target.checked }))
                        }
                        className="size-4 accent-primary"
                      />
                      <span className="text-xs font-medium text-muted-foreground">
                        I offer this
                      </span>
                    </label>
                  </div>
                  {isOn && (
                    <div className="mt-3 flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground">
                        {currencySymbol}
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={prices[card.id] ?? card.defaultMajor}
                        onChange={(e) =>
                          setPrices((prev) => ({ ...prev, [card.id]: e.target.value }))
                        }
                        className="num w-32 rounded-md border border-border bg-surface px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-ring/30"
                        placeholder={card.defaultMajor}
                        aria-label={`${card.label} price in ${currency}`}
                      />
                      <span className="text-xs text-muted-foreground">{card.pricingLabel}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
      )}

      <div className="flex items-center justify-between gap-2">
        <SkipButton onClick={() => navigate('/onboarding/services')}>Back</SkipButton>
        <div className="flex items-center gap-3">
          <SkipButton onClick={() => navigate('/onboarding/runs')}>Skip for now</SkipButton>
          <PrimaryButton
            onClick={() => void handleContinue()}
            disabled={busy || !Object.values(enabled).some(Boolean)}
          >
            {busy && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
            Continue
            <ArrowRight className="size-4" strokeWidth={1.75} />
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 5 — Runs / capacity (skippable for pure grooming tenants)
// ─────────────────────────────────────────────────────────────────────────────

function RunsCapacityStep({ state }: { state: PersistedState }) {
  const navigate = useNavigate();
  const skip = state.businessType === 'grooming';

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Capacity</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {skip
            ? 'Your business is grooming-only — no runs or daycare capacity needed. Skip to brand.'
            : "We'll set up sensible defaults. You can fine-tune in Settings → Runs / Daycare capacity."}
        </p>
      </div>
      <div className="rounded-lg bg-secondary/30 px-4 py-3 text-sm">
        {skip ? 'No capacity setup required.' : 'Defaults will be created automatically.'}
      </div>
      <div className="flex justify-between gap-2">
        <SkipButton onClick={() => navigate('/onboarding/rates')}>Back</SkipButton>
        <PrimaryButton onClick={() => navigate('/onboarding/brand')}>
          Continue
          <ArrowRight className="size-4" strokeWidth={1.75} />
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 6 — Brand voice + logo / palette
// ─────────────────────────────────────────────────────────────────────────────

const PALETTES: ReadonlyArray<{ label: string; color: string; description: string }> = [
  { label: 'Warm sand', color: 'oklch(0.70 0.13 60)', description: 'Earthy + welcoming' },
  { label: 'Sage', color: 'oklch(0.68 0.10 150)', description: 'Calm + natural' },
  { label: 'Cobalt', color: 'oklch(0.45 0.18 250)', description: 'Trusted + classic' },
  { label: 'Coral', color: 'oklch(0.72 0.16 25)', description: 'Vibrant + friendly' },
  { label: 'Charcoal', color: 'oklch(0.30 0.02 270)', description: 'Modern + premium' },
  { label: 'Lavender', color: 'oklch(0.78 0.08 295)', description: 'Soft + pastel' },
];

function BrandStep({
  state,
  setState,
}: {
  state: PersistedState;
  setState: (patch: Partial<PersistedState>) => void;
}) {
  const navigate = useNavigate();
  const [savingNotes, setSavingNotes] = useState(false);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const tone = state.brandTone < 50 ? 'formal' : 'casual';
      const warmth = state.brandWarmth < 50 ? 'professional' : 'warm';
      const length = state.brandLength < 50 ? 'concise' : 'detailed';
      const composed =
        `tone:${tone} warmth:${warmth} length:${length}\n\n${state.brandNotes}`.trim();
      await onboardingPatchStep({ brandVoiceNotes: composed });
    } catch {
      // Non-fatal — settings page can re-save.
    } finally {
      setSavingNotes(false);
    }
    navigate('/onboarding/payments');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Your brand voice</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          This calibrates how the AI Copilot writes on your behalf — confirmations, replies, the
          tone of automated emails. You can revise any time in Settings → Copilot.
        </p>
        <VideoLink label="How brand voice flows through" src="" />
      </div>

      <section className="space-y-4">
        <Slider
          label="Tone"
          leftLabel="Formal"
          rightLabel="Casual"
          value={state.brandTone}
          onChange={(v) => setState({ brandTone: v })}
        />
        <Slider
          label="Warmth"
          leftLabel="Professional"
          rightLabel="Warm"
          value={state.brandWarmth}
          onChange={(v) => setState({ brandWarmth: v })}
        />
        <Slider
          label="Length"
          leftLabel="Concise"
          rightLabel="Detailed"
          value={state.brandLength}
          onChange={(v) => setState({ brandLength: v })}
        />
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
            Anything else? (Copilot will use this)
          </span>
          <textarea
            value={state.brandNotes}
            onChange={(e) => setState({ brandNotes: e.target.value })}
            placeholder="e.g. We always sign off with 'Wags + woofs, the Riverside team'"
            rows={3}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring/30"
          />
        </label>
      </section>

      <section>
        <h3 className="mb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          Pick a primary colour
        </h3>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
          {PALETTES.map((p) => {
            const active = state.primaryColor === p.color;
            return (
              <button
                key={p.label}
                type="button"
                onClick={() => setState({ primaryColor: p.color })}
                className={cn(
                  'group flex flex-col items-center gap-1 rounded-lg border p-2 press',
                  active ? 'border-primary ring-2 ring-primary/30' : 'border-border',
                )}
                title={p.description}
              >
                <div
                  className="size-8 rounded-full ring-1 ring-border"
                  style={{ background: p.color }}
                />
                <div className="text-[10px] font-medium">{p.label}</div>
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          You can upload a logo later in Settings → Branding.
        </p>
      </section>

      <div className="flex justify-between gap-2">
        <SkipButton onClick={() => navigate('/onboarding/runs')}>Back</SkipButton>
        <PrimaryButton onClick={() => void saveNotes()} disabled={savingNotes}>
          {savingNotes && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
          Continue
          <ArrowRight className="size-4" strokeWidth={1.75} />
        </PrimaryButton>
      </div>
    </div>
  );
}

function Slider({
  label,
  leftLabel,
  rightLabel,
  value,
  onChange,
}: {
  label: string;
  leftLabel: string;
  rightLabel: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </span>
        <span className="text-[11px] text-muted-foreground">
          {leftLabel} · {rightLabel}
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-primary"
        aria-label={label}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Step 7 — Payments (Stripe Connect, but "do later" is fine)
// ─────────────────────────────────────────────────────────────────────────────

function PaymentsStep({ onFinish }: { onFinish: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('return') !== '1') return;
    setPolling(true);
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      try {
        const me = await getMe();
        if (cancelled) return;
        if (me.activeAccount?.stripeChargesEnabled === 1) {
          onFinish();
          return;
        }
      } catch {
        // Swallow.
      }
      timer = setTimeout(tick, 1000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [onFinish]);

  const connect = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { url } = await postConnectOnboardingLink();
      window.location.href = url;
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Could not start Stripe onboarding.');
      setSubmitting(false);
    }
  }, []);

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <div className="grid size-10 place-items-center rounded-xl bg-primary/10 text-primary">
          <CreditCard className="size-5" strokeWidth={1.75} />
        </div>
        <h2 className="font-heading text-2xl font-medium tracking-tight">Payments</h2>
      </div>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Connect Stripe so customers can pay you online. We take{' '}
        <strong className="text-foreground">zero transaction fees</strong> — only Stripe's standard
        processing rate.
      </p>
      <div className="rounded-lg border border-border bg-background/60 p-4 text-sm">
        <div className="font-medium">Not ready right now?</div>
        <div className="mt-1 text-xs text-muted-foreground">
          Connecting takes about 5 minutes and you'll need your business ID + bank details. You can
          do it later from Settings → Payments without losing any setup work.
        </div>
      </div>
      {polling && (
        <p className="rounded-lg bg-secondary/40 px-3 py-2 text-sm text-muted-foreground">
          <Loader2 className="mr-1.5 inline size-4 animate-spin" strokeWidth={1.75} />
          Confirming your Stripe setup…
        </p>
      )}
      {submitError && (
        <p className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {submitError}
        </p>
      )}
      <div className="flex justify-between gap-2">
        <SkipButton onClick={onFinish}>Skip for now — take me to the app</SkipButton>
        <PrimaryButton onClick={() => void connect()} disabled={submitting}>
          {submitting && <Loader2 className="size-4 animate-spin" strokeWidth={1.75} />}
          Connect with Stripe
          <ArrowRight className="size-4" strokeWidth={1.75} />
        </PrimaryButton>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared primitives
// ─────────────────────────────────────────────────────────────────────────────

function PrimaryButton({
  onClick,
  children,
  disabled,
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground',
        'px-4 py-2.5 text-sm font-medium press disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      {children}
    </button>
  );
}

function SkipButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-foreground press"
    >
      {children}
    </button>
  );
}

function VideoLink({ label, src }: { label: string; src: string }) {
  const [open, setOpen] = useState(false);
  // Swapping in real videos later is a one-line `src` change. The link
  // surfaces even when src is empty so the affordance is consistent — opening
  // shows a "coming soon" placeholder rather than playing.
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-4 hover:underline"
      >
        <Play className="size-3" strokeWidth={2} />
        {label}
      </button>
      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-6"
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-2xl bg-surface p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium">{label}</h3>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close">
                <X className="size-4" strokeWidth={1.75} />
              </button>
            </div>
            {src ? (
              <video src={src} controls className="w-full rounded-lg" />
            ) : (
              <div className="grid aspect-video place-items-center rounded-lg bg-secondary text-sm text-muted-foreground">
                <div className="flex flex-col items-center gap-2">
                  <Sparkles className="size-6 text-primary" strokeWidth={1.75} />
                  Walkthrough coming soon.
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// Avoid unused import warning when MemoizedComponent removed later.
void useMemo;
void Palette;
